import {
  ComponentProps,
  FC,
  PropsWithChildren,
  useCallback,
  useRef,
} from "react";
import { Button } from "@roole/components-library";

import styles from "./DownloadButton.module.scss";

interface DownloadOptions {
  filename?: string;
}

interface DownloadButtonProps
  extends ComponentProps<typeof Button>,
    PropsWithChildren {
  onDownload(): Promise<Blob | unknown>;
  onError?(error: unknown): void;
  options?: DownloadOptions;
  disabled?: boolean;
}

export const DownloadButton: FC<DownloadButtonProps> = ({
  children,
  onDownload,
  onError,
  options = { filename: "file" },
  disabled = false,
  ...props
}) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  const downloadHandler = useCallback(async () => {
    try {
      const file = (await onDownload()) as Blob;

      if (linkRef.current) {
        linkRef.current.href = URL.createObjectURL(file);
        linkRef.current.download = `${options?.filename ?? "file"}.jpg`;
        linkRef.current.click();
      } else {
        throw new Error(`ReferenceError: 'linkRef' is not defined`);
      }
    } catch (err) {
      typeof onError === "function" && onError(err);
    }
  }, [linkRef, onDownload]);

  return (
    <div className={styles["download-button"]}>
      <a style={{ display: "none" }} href="empty" ref={linkRef}></a>
      <Button
        customAttributes={{
          className: "center-label",
          type: "button",
          disabled,
        }}
        onClick={downloadHandler}
        {...props}
      >
        {children}
      </Button>
    </div>
  );
};
